import { useState, useEffect } from 'react';
import Header from '../../components/Global/Header';
import { register, login, forgotPassword } from '../../services/auth';
import { useNavigate } from 'react-router-dom';
import { authSuccess } from '../../slices/auth/authSlice';
import { useDispatch, useSelector } from 'react-redux';
import GoogleIcon from '../../assets/google-g.svg';
import AppleIcon from '../../assets/apple.svg';
import { ArrowRight, Check, X } from 'react-feather';
import TextField from '@mui/material/TextField';
import { isValidEmail, isValidPassword } from '../../utils/validEmail';
import InputAdornment from '@mui/material/InputAdornment';
import { Tooltip } from '@mui/material';
import Plane from '../../assets/images/plane.svg';
import { getUserIANA } from '../../utils/getUserIANA';
import { HeaderType } from '../../types/Header';
import { hapticsImpactLight } from '../../utils/haptics';
import ThreeWave from '../../components/Home/ThreeWave';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { App } from '@capacitor/app';
import IntercomButton from '../../components/Global/IntercomButton';
import EllipsisLoader from '../../components/Misc/EllipsisLoader';
import { Ora } from '../../assets/svgs/Ora';
import { RootState } from '../../store/store';
import SignupDisplay from './SignupDisplay';
import {
  fetchCohortFromReferralId,
  fetchSchoolDomains,
} from '../../services/schools';
enum LastUsedAuth {
  Email = 'email',
  Apple = 'apple',
  Google = 'google',
}

const Auth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [isForgotPasswordSent, setIsForgotPasswordSent] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState(false);
  const [authErrorMessage, setAuthErrorMessage] = useState('');
  const [lastUsedAuth, setLastUsedAuth] = useState<LastUsedAuth | null>(null);
  const [showGetAppFlyout, setShowGetAppFlyout] = useState(false);
  const [isAmbassador, setIsAmbassador] = useState(false);
  const [isGroupSignup, setIsGroupSignup] = useState(false);
  const [getAppFlyoutType, setGetAppFlyoutType] = useState(''); // 'apple' | 'google'
  const [cohort, setCohort] = useState('');
  const [schoolDomains, setSchoolDomains] = useState<string[]>([]);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.isAuthenticated
  );

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const signup = urlParams.get('signup');
    const plan = urlParams.get('plan');
    const refIdParam = urlParams.get('refId');
    const ambassador = urlParams.get('ambassador');
    const cohortParam = urlParams.get('cohort');

    const storedReferralId = sessionStorage.getItem('ora-refId');
    if (refIdParam) {
      sessionStorage.setItem('ora-refId', refIdParam);
      setIsLogin(false);
    }

    if (plan) {
      sessionStorage.setItem('ora-plan', plan);
      if (plan === 'GROUP') {
        setIsGroupSignup(true);
      }
    }
    if (signup === 'true') {
      setIsLogin(false);
    }
    if (ambassador === 'true') {
      setIsAmbassador(true);
    }
    // Check and set cohort if valid
    const storedCohort = localStorage.getItem('ora-cohort');
    if (cohortParam) {
      setIsLogin(false);
      localStorage.setItem('ora-cohort', cohortParam);
      setCohort(cohortParam);
    } else if (storedCohort) {
      setIsLogin(false);
      setCohort(storedCohort);
    } else if (refIdParam || storedReferralId) {
      const refId = refIdParam || storedReferralId || '';
      // If no cohort is provided, try to infer it from the referralId
      fetchCohortFromReferralId(refId).then((cohort) => {
        setIsLogin(false);
        setCohort(cohort);
        localStorage.setItem('ora-cohort', cohort);
      });
    }

    if (
      Capacitor.getPlatform() === 'ios' ||
      Capacitor.getPlatform() === 'android'
    ) {
      App.addListener('appUrlOpen', (event) => {
        const url = new URL(event.url);
        const authToken = url.searchParams.get('authToken');
        const refreshToken = url.searchParams.get('refreshToken');
        const userId = url.searchParams.get('userId');

        if (authToken && refreshToken && userId) {
          // *** Dispatch authSuccess if userId is available ***
          dispatch(authSuccess({ authToken, refreshToken, userId }));
          Browser.close();
        }
      });
    }

    const seenGetAppFlyout = localStorage.getItem('ora-seen-get-app-flyout');
    if (!seenGetAppFlyout && isIOS()) {
      localStorage.setItem('ora-seen-get-app-flyout', 'true');
      setGetAppFlyoutType('apple');
      setShowGetAppFlyout(true);
    }
    if (!seenGetAppFlyout && isAndroid()) {
      localStorage.setItem('ora-seen-get-app-flyout', 'true');
      setGetAppFlyoutType('google');
      setShowGetAppFlyout(true);
    }

    const lastUsedAuth = localStorage.getItem('last-used-auth');
    if (lastUsedAuth) {
      setLastUsedAuth(lastUsedAuth as LastUsedAuth);
    }
  }, []);

  useEffect(() => {
    const getSchoolDomains = async () => {
      try {
        const domains = await fetchSchoolDomains();
        setSchoolDomains(domains);
      } catch (error) {
        console.error('Error fetching school domains:', error);
      }
    };
    getSchoolDomains();
  }, []);

  useEffect(() => {
    // For OAuth
    const urlParams = new URLSearchParams(window.location.search);
    const urlAuthToken = urlParams.get('authToken');
    const urlRefreshToken = urlParams.get('refreshToken');
    const urlUserId = urlParams.get('userId');
    const errorParam = urlParams.get('error');

    // Handle OAuth Redirect (Web)
    if (urlAuthToken && urlRefreshToken && urlUserId) {
      // *** Dispatch authSuccess if userId is available ***
      dispatch(
        authSuccess({
          authToken: urlAuthToken,
          refreshToken: urlRefreshToken,
          userId: urlUserId,
        })
      );
      navigate('/', { replace: true });
    } else if (urlAuthToken && urlRefreshToken) {
      handleOAuthSuccess(urlAuthToken, urlRefreshToken);
    }

    if (errorParam === 'ambassador-email-not-allowed') {
      setAuthError(true);
      setAuthErrorMessage('Peer Advocate signups require a .edu email.');
      navigate('/auth', { replace: true });
    }

    if (errorParam === 'account-exists') {
      setAuthError(true);
      setAuthErrorMessage(
        'Account already exists. Please sign in with your existing account.'
      );
      navigate('/auth', { replace: true });
    }

    if (isAuthenticated) {
      navigate('/');
    }
  }, [navigate, isAuthenticated, dispatch]);

  useEffect(() => {
    setIsForgotPasswordSent(false);
  }, [isForgotPassword, isLogin]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setAuthError(false);
    setAuthErrorMessage('');

    try {
      let response;
      if (isLogin) {
        const userCredentials = { email, password };
        response = await login(userCredentials);
        if (!response?.success) {
          setAuthError(true);
          setAuthErrorMessage(
            response?.error || 'An error occurred during login.'
          );
        }
      } else {
        // --- Ambassador .edu Email Validation ---
        if (
          isAmbassador &&
          !(
            email.endsWith('.edu') ||
            schoolDomains.includes(email.split('@')[1])
          )
        ) {
          setAuthError(true);
          setAuthErrorMessage(
            'Peer Advocates must sign up with a school email.'
          );
          setIsLoading(false);
          return;
        }
        // --- End Validation ---

        const isMobileApp =
          Capacitor.getPlatform() === 'ios' ||
          Capacitor.getPlatform() === 'android';
        const urlParams = new URLSearchParams(window.location.search);
        let referralId = urlParams.get('refId');
        if (!referralId) {
          referralId = sessionStorage.getItem('ora-refId');
          sessionStorage.removeItem('ora-refId');
        }
        let buyIntent = urlParams.get('ora-plan');
        if (!buyIntent) {
          buyIntent = sessionStorage.getItem('ora-plan');
          sessionStorage.removeItem('ora-plan');
        }
        const userCredentials = {
          email,
          password,
          timezone: getUserIANA(),
          isMobileApp,
          referralId,
          buyIntent,
          cohort,
          ambassador: isAmbassador,
        };
        const registerResponse = await register(userCredentials);

        if (!registerResponse?.success) {
          setAuthError(true);
          setAuthErrorMessage(
            registerResponse?.error || 'An error occurred during login.'
          );
        }

        // After successful registration, log the user in
        response = await login({ email, password });
        if (!response?.success) {
          setAuthError(true);
          setAuthErrorMessage(
            response?.error || 'An error occurred during login.'
          );
        }
        // Success is handled by useEffect watching isAuthenticated
      }
    } finally {
      localStorage.removeItem('ora-cohort');
      setIsLoading(false);
    }
  };

  // Fallback for OAuth flow if userId is missing in URL (shouldn't happen)
  // This function only sets tokens locally, ProtectedRoute will fetch full user data
  const handleOAuthSuccess = (authToken: string, refreshToken: string) => {
    localStorage.setItem('authToken', authToken); // Manually set for now
    localStorage.setItem('refreshToken', refreshToken);
    navigate('/', { replace: true });
    window.location.reload();
  };

  const handleOAuthLogin = async (oathType: 'apple' | 'google') => {
    let backendUrl = import.meta.env.VITE_BACKEND_URL;
    if (!backendUrl) {
      backendUrl = process.env.VITE_BACKEND_URL;
    }
    const urlParams = new URLSearchParams(window.location.search);
    let referralId = urlParams.get('refId');
    if (!referralId) {
      referralId = sessionStorage.getItem('ora-refId');
    }
    let buyIntent = urlParams.get('ora-plan');
    if (!buyIntent) {
      buyIntent = sessionStorage.getItem('ora-plan');
      sessionStorage.removeItem('ora-plan');
    }
    hapticsImpactLight();
    localStorage.setItem('last-used-auth', oathType);
    if (
      Capacitor.getPlatform() === 'ios' ||
      Capacitor.getPlatform() === 'android'
    ) {
      let url = backendUrl + `/auth/${oathType}?isMobileApp=true`;
      if (referralId) {
        sessionStorage.removeItem('ora-refId');
        url += `&refId=${referralId}`;
      }
      if (cohort) {
        url += `&cohort=${cohort}`;
      }
      await Browser.open({ url });
    } else {
      let url = backendUrl + `/auth/${oathType}`;
      const params: string[] = [];
      if (referralId) {
        sessionStorage.removeItem('ora-refId');
        params.push(`refId=${encodeURIComponent(referralId)}`);
      }
      if (buyIntent) {
        sessionStorage.removeItem('ora-plan');
        params.push(`buyIntent=${encodeURIComponent(buyIntent)}`);
      }
      if (cohort) {
        params.push(`cohort=${encodeURIComponent(cohort)}`);
      }
      if (isAmbassador) {
        params.push(`ambassador=true`);
      }
      if (params.length > 0) {
        url += `?${params.join('&')}`;
      }
      window.location.href = url;
    }
    localStorage.removeItem('ora-cohort');
  };

  const handleForgotPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setAuthError(false);
    if (!isValidEmail(email)) return;

    setIsLoading(true);
    try {
      const response = await forgotPassword(email);
      if (response.success) {
        setIsForgotPasswordSent(true);
      }
    } catch (error) {
      setAuthError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const renderAppleSignIn = () => {
    if (isAmbassador) return null;
    return (
      <button
        className="button button--oauth button--oauth--apple"
        onClick={() => handleOAuthLogin('apple')}
        aria-label={
          isLogin ? 'Create Account with Apple' : 'Sign in with Apple'
        }
      >
        <img src={AppleIcon} alt="Apple Logo" />
        <span>{isLogin ? 'Sign in with Apple' : 'Sign up with Apple'}</span>
      </button>
    );
  };

  const renderGoogleSignIn = () => {
    return (
      <button
        className="button button--oauth button--oauth--google"
        onClick={() => handleOAuthLogin('google')}
        aria-label={
          isLogin ? 'Create Account with Google' : 'Sign in with Google'
        }
      >
        <img src={GoogleIcon} alt="Google Logo" />
        {isLogin ? 'Sign in with Google' : 'Sign up with Google'}
      </button>
    );
  };

  const renderEmailSignIn = () => {
    const isSchoolEmailRequired = isAmbassador && !isLogin;
    const isSchoolEmailValid =
      schoolDomains.includes(email.split('@')[1]) || email.endsWith('.edu');
    const showSchoolEmailError =
      isSchoolEmailRequired && email.length > 0 && !isSchoolEmailValid;
    const showGenericEmailError =
      !isSchoolEmailRequired && email.length > 0 && !isValidEmail(email);
    const isEmailValidForSubmit =
      isValidEmail(email) && (!isSchoolEmailRequired || isSchoolEmailValid);

    return (
      <>
        {authError && (
          <p className="status status--error">
            {authErrorMessage ?? 'An error has occurred. Please try again.'}
          </p>
        )}
        <form onSubmit={handleSubmit}>
          <div className="field m-t-0">
            <TextField
              id="email"
              fullWidth
              label={isSchoolEmailRequired ? 'School Email' : 'Email'}
              type="email"
              variant="outlined"
              value={email}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setEmail(event.target.value);
              }}
              InputProps={{
                endAdornment: isEmailValidForSubmit ? (
                  <Tooltip
                    title={
                      isSchoolEmailRequired
                        ? 'School email is valid.'
                        : 'Email is valid.'
                    }
                    enterTouchDelay={0}
                  >
                    <div className="field-validator field-validator--pass">
                      <InputAdornment position="end">
                        <Check />
                      </InputAdornment>
                    </div>
                  </Tooltip>
                ) : (
                  <>
                    {(showGenericEmailError || showSchoolEmailError) && (
                      <Tooltip
                        title={
                          showSchoolEmailError
                            ? 'School email required'
                            : 'Email must be valid.'
                        }
                        enterTouchDelay={0}
                      >
                        <div className="field-validator field-validator--error"></div>
                      </Tooltip>
                    )}
                  </>
                ),
              }}
            />
          </div>
          <div
            className={`field ${isLogin ? 'hidden field--with-forgot' : 'hidden field--with-forgot'} ${email || password.length > 0 ? 'visible' : ''}`}
          >
            <TextField
              id="password"
              fullWidth
              label="Password"
              type="password"
              variant="outlined"
              value={password}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setPassword(event.target.value);
              }}
              InputProps={{
                endAdornment: isValidPassword(password) ? (
                  <Tooltip
                    title="Password is at least 8 characters."
                    enterTouchDelay={0}
                  >
                    <div className="field-validator field-validator--pass">
                      <InputAdornment position="end">
                        <Check />
                      </InputAdornment>
                    </div>
                  </Tooltip>
                ) : (
                  <>
                    {password.length > 0 && (
                      <Tooltip
                        title="Password must be at least 8 characters."
                        enterTouchDelay={0}
                      >
                        <div className="field-validator"></div>
                      </Tooltip>
                    )}
                  </>
                ),
              }}
            />
            {isLogin && (
              <button
                type="button"
                className="button button--forgot"
                onClick={() => setIsForgotPassword(true)}
              >
                Forgot password?
              </button>
            )}
          </div>
          {!isLogin && (
            <div
              className={`field hidden ${email && password.length > 0 ? 'visible' : ''}`}
            >
              <TextField
                id="password-confirm"
                fullWidth
                type="password"
                label="Confirm Password"
                variant="outlined"
                value={passwordConfirm}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setPasswordConfirm(event.target.value);
                }}
                InputProps={{
                  endAdornment:
                    isValidPassword(password) &&
                    password === passwordConfirm ? (
                      <Tooltip title="Passwords match." enterTouchDelay={0}>
                        <div className="field-validator field-validator--pass">
                          <InputAdornment position="end">
                            <Check />
                          </InputAdornment>
                        </div>
                      </Tooltip>
                    ) : (
                      <>
                        {passwordConfirm.length > 0 && (
                          <Tooltip
                            title="Passwords must match."
                            enterTouchDelay={0}
                          >
                            <div className="field-validator"></div>
                          </Tooltip>
                        )}
                      </>
                    ),
                }}
              />
            </div>
          )}
          <div
            className={`submit-wrapper m-t-1 hidden ${(isLogin && email && password) || (!isLogin && email && password && passwordConfirm) ? 'visible' : ''}`}
          >
            <button
              className={`button button--submit button--glass ${isLoading ? 'is-loading' : ''}`}
              type="submit"
              onClick={() => {
                localStorage.setItem('last-used-auth', 'email');
                hapticsImpactLight();
              }}
              disabled={
                isLoading ||
                !isValidPassword(password) ||
                !isEmailValidForSubmit ||
                (!isLogin && password !== passwordConfirm)
              }
            >
              {isLogin ? 'Sign In' : 'Create Account'}
              {isLoading && <EllipsisLoader />}
            </button>
          </div>
        </form>
      </>
    );
  };

  const renderDefaultSignIn = () => {
    return (
      <>
        {!isAmbassador && renderAppleSignIn()}
        {!isAmbassador && renderGoogleSignIn()}
        {!isAmbassador && <div className="or">or</div>}
        {renderEmailSignIn()}
      </>
    );
  };

  const renderLastUsedAuth = () => {
    return (
      <>
        <div className="last-used">Last Used</div>
        {lastUsedAuth === LastUsedAuth.Google && (
          <div className="last-used-oauth">
            {renderGoogleSignIn()}
            <div className="or">or</div>
            {renderAppleSignIn()}
            {renderEmailSignIn()}
          </div>
        )}
        {lastUsedAuth === LastUsedAuth.Apple && (
          <div className="last-used-oauth">
            {renderAppleSignIn()}
            <div className="or">or</div>
            {renderGoogleSignIn()}
            {renderEmailSignIn()}
          </div>
        )}
        {lastUsedAuth === LastUsedAuth.Email && (
          <div className="last-used-email">
            {renderEmailSignIn()}
            <div className="or m-t-0">or</div>
            {renderGoogleSignIn()}
            {renderAppleSignIn()}
          </div>
        )}
      </>
    );
  };

  const renderSignIn = () => {
    return <>{lastUsedAuth ? renderLastUsedAuth() : renderDefaultSignIn()}</>;
  };

  const renderSignUp = () => {
    return <>{renderDefaultSignIn()}</>;
  };

  const isIOS = () => {
    return (
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    );
  };

  const isAndroid = () => {
    return (
      /Android/.test(navigator.userAgent) ||
      (navigator.platform.toLowerCase().includes('linux') &&
        navigator.maxTouchPoints > 1)
    );
  };

  return (
    <div
      className={`auth ${isLogin ? 'auth--login' : 'auth--signup'} ${isAmbassador ? 'auth--ambassador' : ''} ${cohort ? 'auth--cohort' : ''}`}
    >
      {isAmbassador && (
        <div className="ambassador-banner">
          <p>You're using a Peer Advocate link. Please sign up to continue.</p>
        </div>
      )}
      <Header type={HeaderType.generic} />
      <div className="auth_body">
        <div className="auth-panel-wrapper">
          <div
            className={`auth-panel ${isForgotPasswordSent ? 'auth-panel--email-sent' : ''}`}
          >
            <div className="mobile-head">
              <div className="logo">
                <Ora />
              </div>
            </div>
            {!isForgotPassword && (
              <>
                <h2>{isLogin ? 'Sign in' : 'Create account'}</h2>
                {isLogin ? renderSignIn() : renderSignUp()}
                {isLogin && (
                  <p className="terms">
                    By using Ora AI, I agree to Ora AI's{' '}
                    <a
                      style={{ color: 'var(--type-body-inverse)' }}
                      href="https://www.oraai.com/terms"
                      target="_blank"
                    >
                      Terms of Use
                    </a>{' '}
                    and{' '}
                    <a
                      style={{ color: 'var(--type-body-inverse)' }}
                      href="https://www.oraai.com/privacy"
                      target="_blank"
                    >
                      Privacy Policy
                    </a>
                    .
                  </p>
                )}
                {!isLogin && (
                  <p className="terms">
                    By continuing, you acknowledge that you understand and agree
                    to the{' '}
                    <a
                      style={{ color: 'var(--type-body-inverse)' }}
                      href="https://www.oraai.com/terms"
                      target="_blank"
                    >
                      Terms of Use
                    </a>{' '}
                    and{' '}
                    <a
                      style={{ color: 'var(--type-body-inverse)' }}
                      href="https://www.oraai.com/privacy"
                      target="_blank"
                    >
                      Privacy Policy
                    </a>
                    .
                  </p>
                )}
                <div
                  className="question"
                  onClick={() => {
                    setIsLogin(!isLogin);
                    setAuthError(false);
                    setAuthErrorMessage('');
                    hapticsImpactLight();
                  }}
                  role="button"
                >
                  {isLogin
                    ? "Don't have an account?"
                    : 'Already have an account?'}
                </div>
                <button
                  className="button button--link--glass"
                  onClick={() => {
                    setIsLogin(!isLogin);
                    setAuthError(false);
                    setAuthErrorMessage('');
                    hapticsImpactLight();
                  }}
                >
                  {isLogin ? 'Create account' : 'Sign in'}
                  <ArrowRight />
                </button>
              </>
            )}
            {isForgotPassword && (
              <>
                {isForgotPasswordSent && (
                  <img
                    src={Plane}
                    alt="Paper airplane"
                    className="email-sent"
                  />
                )}
                <h2>
                  {isForgotPasswordSent
                    ? 'Reset email has been sent'
                    : 'Reset password'}
                </h2>
                <p className="m-b-1-50">
                  {isForgotPasswordSent
                    ? 'Please check your inbox.'
                    : 'Enter your email to receive a password reset link.'}
                </p>
                {!isForgotPasswordSent && (
                  <form
                    className="overflow-visible"
                    onSubmit={handleForgotPassword}
                  >
                    <TextField
                      id="email"
                      fullWidth
                      label="Email"
                      type="email"
                      variant="outlined"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      InputProps={{
                        endAdornment: isValidEmail(email) ? (
                          <Tooltip title="Email is valid." enterTouchDelay={0}>
                            <div className="field-validator field-validator--pass">
                              <InputAdornment position="end">
                                <Check />
                              </InputAdornment>
                            </div>
                          </Tooltip>
                        ) : (
                          <>
                            {email.length > 0 && (
                              <Tooltip
                                title="Email must be valid."
                                enterTouchDelay={0}
                              >
                                <div className="field-validator"></div>
                              </Tooltip>
                            )}
                          </>
                        ),
                      }}
                    />
                    <button
                      className="button button--glass m-t-1-50"
                      type="submit"
                      onClick={hapticsImpactLight}
                      disabled={isLoading || !isValidEmail(email)}
                    >
                      Send Reset Link
                    </button>
                  </form>
                )}
                <button
                  className={`button m-t-1 ${isForgotPasswordSent ? 'button--glass' : 'button--link--glass'}`}
                  onClick={() => {
                    setIsForgotPassword(false);
                    hapticsImpactLight();
                  }}
                >
                  Back to Sign In
                </button>
              </>
            )}
          </div>
        </div>
        <SignupDisplay
          cohort={cohort}
          isGroupSignup={isGroupSignup}
          isLogin={isLogin}
        />
      </div>
      {!Capacitor.isNativePlatform() && (
        <div className={`get-app-flyout ${showGetAppFlyout ? 'float-in' : ''}`}>
          <h3>Ora is better on the app.</h3>
          {getAppFlyoutType === 'apple' && (
            <>
              <a
                className="button button--oauth button--oauth--apple"
                href="https://apps.apple.com/us/app/ora-ai-guided-step-prep/id6484319369"
                target="blank"
              >
                <img src={AppleIcon} alt="Apple Logo" />
                <span>Download on the App Store</span>
              </a>
            </>
          )}
          {getAppFlyoutType === 'google' && (
            <>
              <a
                className="button button--oauth button--oauth--google"
                href="https://play.google.com/store/apps/details?id=com.oraai.app"
                target="blank"
              >
                <img src={GoogleIcon} alt="Google Logo" />
                <span>Download on the Play Store</span>
              </a>
            </>
          )}
          <button
            className="button button--close"
            onClick={() => setShowGetAppFlyout(false)}
          >
            <X />
          </button>
        </div>
      )}
      <ThreeWave showLabels={true} transitionStarted={false} />
      <IntercomButton />
    </div>
  );
};

export default Auth;
